import { Controller } from 'stimulus'
import { DateTime } from 'luxon'
import { useDispatch } from 'stimulus-use'
import { isDesktopView } from '~/controllers/booking/helpers'

export default class extends Controller {
  static values = {
    url: String,
    monthView: Boolean,
  }

  static targets = ['jumpDate']

  connect() {
    useDispatch(this)
    this.headsUpTimer = null
    this.headsUpCleanup = null
    // The fetch and the login-check callback can resolve after this page is gone (Turbo visit):
    // they must then do nothing, or they'd act on the next booking page's modal.
    this.abortController = new AbortController()
    const { signal } = this.abortController

    fetch(this.urlValue, { signal })
      .then((response) => {
        return response.json()
      })
      .then((data) => {
        if (signal.aborted) return
        if (!data.date) {
          this.dispatch('noAvailability')
          return
        }

        if (!data.jump) {
          return
        }

        const date = DateTime.fromISO(data.date)
        const today = DateTime.now()

        // Skip the jump modal when the next available date is in the current month
        // and we're either on mobile (no week view) or in month view (full month visible)
        if (
          (!isDesktopView() || this.monthViewValue) &&
          date.year == today.year &&
          date.month == today.month
        ) {
          return
        }

        // If login check is in progress, then add a callback to wait till it's done.
        // Otherwise we'd risk showing the modal twice - before and after relooad - which just looks silly.
        if (typeof window.onLogincheckComplete === 'function') {
          window.onLogincheckComplete(() => signal.aborted || this.jump(data))
        } else {
          this.jump(data)
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') throw error
      })
  }

  jump(data) {
    this.dispatch('jumpToAvailable', { date: data.date })

    this.jumpDateTarget.innerHTML = data.localized_date

    this.headsUpTimer = setTimeout(() => {
      if (this.abortController.signal.aborted) return
      const todayCells = document.querySelectorAll('.fc-day-today')
      const isVisible = (el) =>
        Boolean(el.offsetWidth || el.offsetHeight || el.getClientRects().length)
      const todayVisible = Array.from(todayCells).some(isVisible)

      if (todayVisible) return

      const modal = document.getElementById('modal-scheduling-availability')
      modal.dispatchEvent(new CustomEvent('modal:show'))
      // A "heads up" needs no form: any of these keys acknowledges it.
      const dismiss = (e) => {
        if (!['Enter', 'Escape', 'ArrowLeft', 'ArrowRight'].includes(e.key))
          return
        e.preventDefault()
        this.headsUpCleanup?.()
        modal.dispatchEvent(new CustomEvent('modal:close'))
      }
      this.headsUpCleanup = () => {
        document.removeEventListener('keydown', dismiss, true)
        this.headsUpCleanup = null
      }
      document.addEventListener('keydown', dismiss, true)
      // The modal dispatches modal:hidden after any close (OK, ×, backdrop, key), so the capture-phase
      // key listener never outlives it and can't swallow Enter in the booking form later.
      modal.addEventListener('modal:hidden', () => this.headsUpCleanup?.(), {
        once: true,
      })
    }, 200)
  }

  disconnect() {
    this.abortController?.abort()
    clearTimeout(this.headsUpTimer)
    this.headsUpCleanup?.()
  }
}
